import { siteName } from 'config/site';
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Paper,
  Stack,
  Alert,
} from '@mui/material';
import {
  Close,
  CheckCircle,
  EditNote,
  Speed,
  Palette,
  TrendingUp
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import ForgotPassword from './ForgotPassword';
import MobileFeatureCarousel from './MobileFeatureCarousel';
import HomepageSEOContent from './HomepageSEOContent';
import PageSEO from '../SEO/PageSEO';
import { buildFAQSchema, buildSoftwareAppSchema } from '../SEO/schemas';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { consumeAccessDeniedMessage } from '../../config/accessControl';
import {
  landingHeroBgSx,
  landingTitleGradientSx,
  landingSubtitleSx,
  landingCtaButtonSx,
  landingOutlinedButtonSx,
  landingFeatureCardSx,
  landingFeatureIconSx,
  landingFeatureBadgeSx,
  authAccent,
} from './authStyles';

const MotionBox = motion(Box);

const LandingAuth = ({ onVerificationNeeded, onCodeSent }) => {
  const [authMode, setAuthMode] = useState('landing'); // 'landing', 'login', 'signup', 'forgot'
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [accessDeniedMessage] = useState(() => consumeAccessDeniedMessage());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const features = [
    {
      icon: <EditNote sx={{ fontSize: 40 }} />,
      title: 'AI-Powered Writing',
      description: 'Transform your experience into compelling achievements with intelligent content generation',
      highlight: '10x faster writing'
    },
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: 'No Subscription',
      description: 'Create unlimited resumes for free. Only pay when you download. One payment = 90-day access',
      highlight: 'Pay per download'
    },
    {
      icon: <Palette sx={{ fontSize: 40 }} />,
      title: 'ATS-Optimized',
      description: 'Pass applicant tracking systems with professionally designed, recruiter-approved templates',
      highlight: '95% pass rate'
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: 'Unlimited Editing',
      description: 'Create and edit as many resumes as you want. Perfect them before you pay a single cent',
      highlight: 'Free to create'
    }
  ];

  const handleToggleMode = () => {
    setAuthMode(authMode === 'login' ? 'signup' : 'login');
  };

  const handleForgotPassword = () => {
    setAuthMode('forgot');
  };

  const handleBackToLogin = () => {
    setAuthMode('login');
  };

  const openAuthSheet = (mode) => {
    if (mobileSheetOpen && authMode === mode) return; // Prevent re-opening same sheet
    setAuthMode(mode);
    if (isMobile) {
      setMobileSheetOpen(true);
    }
  };

  const closeAuthSheet = () => {
    if (!mobileSheetOpen) return; // Already closed
    setMobileSheetOpen(false);
    // Reset auth mode after animation completes
    setTimeout(() => {
      setAuthMode('landing');
    }, 200);
  };

  const handleCloseAuth = () => {
    if (isMobile) {
      closeAuthSheet();
    } else {
      setAuthMode('landing');
    }
  };

  // Desktop Hero Section
  const DesktopHero = () => (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        ...landingHeroBgSx,
        '&::before': {
          ...landingHeroBgSx['&::before'],
          animation: 'pulse 4s ease-in-out infinite',
        },
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: 'absolute',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
          top: '10%',
          right: '10%',
          filter: 'blur(60px)',
          animation: 'float 8s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0) scale(1)'
            },
            '50%': {
              transform: 'translateY(30px) scale(1.1)'
            }
          }
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: '80vh' }}>
          {/* Left Side - Hero Content */}
          <Box sx={{ flex: 1, color: 'white' }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                fontWeight: 800,
                mb: 2,
                lineHeight: 1.2,
                ...landingTitleGradientSx,
              }}
            >
              {siteName}
            </Typography>

            <Box>
              <Typography 
                variant="h5"
                sx={{ 
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  fontWeight: 500,
                  ...landingSubtitleSx,
                  mb: 1,
                }}
              >
                Generate → Customize → Download
              </Typography>
              
              <Typography variant="h6" sx={{ fontWeight: 300, mb: 4, opacity: 0.9 }}>
                Your Resume — AI Powered
              </Typography>
            </Box>

            <Box>
              <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', opacity: 0.95, maxWidth: 500 }}>
                Create unlimited resumes for free. Only pay when you're ready to download. 
                No subscriptions, no hidden fees — just professional results.
              </Typography>

              <Stack direction="row" spacing={2} sx={{ mb: 4 }} flexWrap="wrap">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => openAuthSheet('signup')}
                  sx={landingCtaButtonSx}
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => openAuthSheet('login')}
                  sx={landingOutlinedButtonSx}
                >
                  Sign In
                </Button>
                <Button
                  component={Link}
                  to="/guides"
                  variant="text"
                  size="large"
                  startIcon={<MenuBookIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    color: 'rgba(255,255,255,0.75)',
                    py: 1.5,
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      color: 'white',
                      bgcolor: 'rgba(255,255,255,0.08)'
                    }
                  }}
                >
                  Resume Guides
                </Button>
              </Stack>

              <Stack direction="row" spacing={3} sx={{ alignItems: 'center', opacity: 0.9 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle sx={{ fontSize: 20 }} />
                  <Typography variant="body2">No subscription fees</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle sx={{ fontSize: 20 }} />
                  <Typography variant="body2">Pay only when you download</Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

          {/* Right Side - Feature Cards or Auth Card */}
          <Box sx={{ flex: 1, maxWidth: 480 }}>
            <AnimatePresence mode="wait">
              {authMode === 'landing' ? (
                <MotionBox
                  key="features"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.4 }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 2
                    }}
                  >
                    {features.map((feature, index) => (
                      <MotionBox
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1, duration: 0.4 }}
                        whileHover={{ scale: 1.03, y: -5 }}
                      >
                        <Paper
                          sx={{
                            p: 2.5,
                            ...landingFeatureCardSx,
                            textAlign: 'center',
                            height: '100%',
                            cursor: 'default',
                          }}
                        >
                          <Box sx={{ ...landingFeatureIconSx, mb: 1 }}>
                            {React.cloneElement(feature.icon, { sx: { fontSize: 32 } })}
                          </Box>
                          <Box sx={landingFeatureBadgeSx}>
                            <Typography variant="caption" sx={{ color: authAccent, fontWeight: 600, fontSize: '0.65rem' }}>
                              {feature.highlight}
                            </Typography>
                          </Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.9rem' }}>
                            {feature.title}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.7rem', lineHeight: 1.3, display: 'block' }}>
                            {feature.description}
                          </Typography>
                        </Paper>
                      </MotionBox>
                    ))}
                  </Box>
                </MotionBox>
              ) : (
                <MotionBox
                  key="auth"
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ duration: 0.4 }}
                >
                  <Paper
                    elevation={24}
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      bgcolor: 'rgba(30, 41, 59, 0.95)',
                      backdropFilter: 'blur(10px)',
                      position: 'relative',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}
                  >
                    <IconButton
                      onClick={handleCloseAuth}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'rgba(255,255,255,0.7)',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.1)',
                          color: 'white'
                        }
                      }}
                    >
                      <Close />
                    </IconButton>

                    {accessDeniedMessage && (
                      <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5 }}>
                        {accessDeniedMessage}
                      </Alert>
                    )}

                    {authMode === 'login' && (
                      <LoginForm
                        onToggleMode={handleToggleMode}
                        onForgotPassword={handleForgotPassword}
                      />
                    )}
                    {authMode === 'signup' && (
                      <SignUpForm
                        onToggleMode={handleToggleMode}
                        onVerificationNeeded={onVerificationNeeded}
                      />
                    )}
                    {authMode === 'forgot' && (
                      <ForgotPassword 
                        onBackToLogin={handleBackToLogin}
                        onCodeSent={onCodeSent}
                      />
                    )}
                  </Paper>
                </MotionBox>
              )}
            </AnimatePresence>
          </Box>
        </Box>

      </Container>
    </Box>
  );

  // Mobile Auth Modal
  const MobileAuthModal = () => (
    <AnimatePresence mode="wait">
      {mobileSheetOpen && (
        <MotionBox
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            bgcolor: '#1e293b',
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleCloseAuth}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1,
              bgcolor: 'rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.7)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.15)',
                color: 'white'
              }
            }}
          >
            <Close />
          </IconButton>

          {/* Content */}
          <Box 
            sx={{ 
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              p: 3,
              pt: 8
            }}
          >
            {accessDeniedMessage && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5 }}>
                {accessDeniedMessage}
              </Alert>
            )}
            {authMode === 'login' && (
              <LoginForm
                onToggleMode={handleToggleMode}
                onForgotPassword={handleForgotPassword}
              />
            )}
            {authMode === 'signup' && (
              <SignUpForm
                onToggleMode={handleToggleMode}
                onVerificationNeeded={onVerificationNeeded}
              />
            )}
            {authMode === 'forgot' && (
              <ForgotPassword 
                onBackToLogin={handleBackToLogin}
                onCodeSent={onCodeSent}
              />
            )}
          </Box>
        </MotionBox>
      )}
    </AnimatePresence>
  );

  // Mobile Hero Section
  const MobileHero = () => (
    <Box
      sx={{
        minHeight: '100vh',
        height: '100vh',
        overflow: 'auto',
        ...landingHeroBgSx,
        color: 'white',
        position: 'relative',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      <Container sx={{ py: 4, pb: 12 }}>
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem' },
              ...landingTitleGradientSx,
            }}
          >
            {siteName}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              fontWeight: 500,
              ...landingSubtitleSx,
              mb: 1,
            }}
          >
            Generate → Customize → Download
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 300, mb: 3, opacity: 0.9 }}>
            Your Resume — AI Powered
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, opacity: 0.95, lineHeight: 1.7 }}>
            Create unlimited resumes for free. Only pay when you download. No subscriptions, just results.
          </Typography>

          <Stack spacing={2} sx={{ mb: 4 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => openAuthSheet('signup')}
              sx={{ ...landingCtaButtonSx, py: 1.5 }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={() => openAuthSheet('login')}
              sx={{ ...landingOutlinedButtonSx, py: 1.5 }}
            >
              Sign In
            </Button>
            <Button
              component={Link}
              to="/guides"
              variant="text"
              size="large"
              fullWidth
              startIcon={<MenuBookIcon sx={{ fontSize: 18 }} />}
              sx={{
                color: 'rgba(255,255,255,0.7)',
                py: 1.25,
                fontSize: '0.95rem',
                fontWeight: 500,
                borderRadius: 2,
                textTransform: 'none',
                border: '1px solid rgba(255,255,255,0.15)',
                '&:hover': {
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.08)',
                  borderColor: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              Resume Guides
            </Button>
          </Stack>

          <Stack spacing={1.5} sx={{ opacity: 0.9, mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle sx={{ fontSize: 18 }} />
              <Typography variant="body2">No subscription - pay per resume</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle sx={{ fontSize: 18 }} />
              <Typography variant="body2">90-day access included</Typography>
            </Box>
          </Stack>

          {/* Mobile Features - Carousel */}
          <MobileFeatureCarousel />
        </Box>
      </Container>
    </Box>
  );

  const homepageFAQs = [
    { question: `Is ${siteName} completely free?`, answer: 'Creating and editing resumes is completely free — unlimited resumes, unlimited edits. You only pay when you are ready to download your final resume. One payment gives you 90-day access.' },
    { question: 'Are the resumes ATS-friendly?', answer: 'Yes. Every template is designed for ATS compatibility with a 95% pass rate. Our keyword optimization matches your resume to the specific job description automatically.' },
    { question: 'How does the AI write my resume?', answer: 'You provide your job title, company, dates, and a rough description. Our AI (GPT-4) transforms that into polished, quantified achievement bullets with keyword optimization for your target role.' },
    { question: 'How long does it take to create a resume?', answer: 'Most users complete their first resume in 15–30 minutes. The AI handles the heavy writing — you review, edit, and refine.' },
    { question: `Can I use ${siteName} for any industry?`, answer: 'Yes. The AI generates content for any profession — software engineering, marketing, nursing, finance, teaching, operations, and more.' },
  ];

  return (
    <>
      <PageSEO
        title={`${siteName} - Create Professional ATS-Optimized Resumes in Minutes`}
        description="Build professional, ATS-optimized resumes with AI-powered content generation. No subscription required — create unlimited resumes free, pay only when you download. 95% ATS pass rate."
        canonical="/"
        ogType="website"
        schemas={[buildFAQSchema(homepageFAQs), buildSoftwareAppSchema()]}
      />

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 0.5; }
            50% { opacity: 0.8; }
          }
          
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
          
          * {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }
        `}
      </style>

      {isMobile ? (
        <>
          <MobileHero />
          <MobileAuthModal />
        </>
      ) : (
        <DesktopHero />
      )}

      <HomepageSEOContent />
    </>
  );
};

export default LandingAuth;

